import React, { forwardRef, useContext, useRef, useState, useEffect } from 'react';
import {
    useDataModel,
    QueryContext
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { handleProductClick, hasSameDayFilter, impProductPodData, getPageTypeFromPath } from '../util';
import { DefaultGrid } from '../components/DefaultGrid';
import { debounce } from '@thd-olt-functional/utils';
import { ImpressionProvider } from '@thd-olt-component-react/impression';

const DefaultGridComposable = forwardRef((_props, ref) => {
    const { customContext, channel, path } = useContext(ExperienceContext);
    const { variables, varnish, hybridLayoutID } = customContext || {};

    const pageType = getPageTypeFromPath(path);
    const store = useStore({ varnish, online: false });
    const { isCustomerIdentified = false } = useThdCustomer();
    const { isClientResolved } = useContext(QueryContext);
    const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

    // Create internal ref only if no external ref provided
    const internalRef = useRef(null);
    // Use the external ref if provided, otherwise use our internal ref
    const resultsDimensionsRef = ref || internalRef;

    const loadedStore = useRef(store);
    const localizedAndClientLoaded = useRef(false);

    const {
        additionalSearchParams = {},
        keyword = null,
        navParam = null
    } = variables || {};
    const options = {
        variables,
        ssr: true,
        skip: !variables?.navParam && !variables?.keyword
    };
    const { data = {}, loading, error } = useDataModel('searchModel', options) || {};
    const { metadata } = data?.searchModel || {};
    const { contentType } = metadata || {};
    const isCategory = contentType === 'categorypage';

    if (isCategory) {
        return null;
    }

    if (!loading && store?.storeId !== '8119' && variables?.storeId === '8119') {
        localizedAndClientLoaded.current = true;
        loadedStore.current = {
            storeId: store?.storeId,
            zipCode: variables?.zipCode
        };
    } else if (data && !loading && store?.isLocalized) {
        localizedAndClientLoaded.current = true;
        loadedStore.current = {
            storeId: store?.storeId,
            zipCode: store?.zipCode
        };
    }
    const defaultVariables = {
        storeId: loadedStore.current?.storeId,
        skipKPF: true,
        skipSubscribeAndSave: true,
        skipInstallServices: false,
        isBrandPricingPolicyCompliant: localizedAndClientLoaded.current ? isCustomerIdentified : false
    };
    const getUniqueSkus = (products = []) => {
        const uniqueSkus = [...new Set(products.map((product) => product.itemId))];
        return uniqueSkus;
    };
    const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray?.length > 0;
    const getBreakpoint = (width) => {
        if (width >= 1280) { return 1280; }
        if (width >= 1024) { return 1024; }
        return null;
    };
    const LAYOUT_CONFIG = {
        1280: { withCategory: 24, withoutCategory: 12 },
        1024: { withCategory: 18, withoutCategory: 9 }
    };
    const useInGridBannerResize = (isClientSearchModelResolved, hasCategoryCard, uniqueSkus) => {
        const [isInGridBannerBottom, setIsInGridBannerBottom] = useState(false);

        useEffect(() => {
            const onResize = debounce(200, () => {
                if (!isClientSearchModelResolved || typeof window === 'undefined') {
                    return;
                }

                const breakpoint = getBreakpoint(window.innerWidth);
                if (!breakpoint) {
                    return;
                }

                const config = LAYOUT_CONFIG[breakpoint];
                const threshold = hasCategoryCard ? config.withCategory : config.withoutCategory;
                const skuCount = uniqueSkus?.length || 0;

                setIsInGridBannerBottom(skuCount < threshold);
            });

            if (typeof window !== 'undefined') {
                window.addEventListener('resize', onResize);
                onResize();
            }

            return () => {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', onResize);
                }
            };
        }, [isClientSearchModelResolved, hasCategoryCard, uniqueSkus?.length]);

        return isInGridBannerBottom;
    };
    const products = data?.searchModel?.products || [];
    const uniqueSkus = getUniqueSkus(products);
    const isInGridBannerBottom = useInGridBannerResize(
        isClientSearchModelResolved,
        hasCategoryCard,
        uniqueSkus
    );
    const defaultGridProps = {
        additionalSearchParams,
        keyword,
        nValue: navParam,
        storeId: loadedStore.current.storeId,
        pageType,
        handleProductClick,
        ref: resultsDimensionsRef,
        defaultVariables,
        impProductPodData: impProductPodData(hybridLayoutID),
        searchModelData: data,
        searchModelLoading: loading,
        searchModelError: error,
        ...(channel === 'desktop'
            ? {
                productPodHoverDelay: 500,
                isInGridBannerBottom
            }
            : {
                hasSameDayFilter: hasSameDayFilter(data?.searchModel?.taxonomy?.breadCrumbs)
            }
        )
    };

    return (
        <ImpressionProvider data={impProductPodData(hybridLayoutID)}>
            <DefaultGrid {...defaultGridProps} />
        </ImpressionProvider>
    );
});

DefaultGridComposable.displayName = 'DefaultGridComposable';
DefaultGridComposable.dataModel = DefaultGrid.dataModel;

export { DefaultGridComposable };